import { nip19 } from 'nostr-tools';

import { normalizeUrl } from '@/lib/normalizeUrl';
import { sanitizeUrl } from '@/lib/sanitizeUrl';

/**
 * Parse freeform "open" input into an internal route.
 *
 * Accepts: bare NIP-19 identifiers, `nostr:` URIs, URLs from other Nostr
 * clients containing identifiers (njump, habla, yakihonne, …), bare hex
 * event ids, Primal-style /e/<hex> and /p/<hex> links, and plain web URLs
 * (routed to the per-URL highlights page).
 */

const BECH32_TOKEN = /(?:npub|nprofile|note|nevent|naddr)1[023456789acdefghjklmnpqrstuvwxyz]{10,}/;
const HEX_64 = /^[0-9a-f]{64}$/i;

function nip19Path(token: string): string | null {
  try {
    const decoded = nip19.decode(token);
    if (decoded.type === 'nsec') return null;
    return `/${token}`;
  } catch {
    return null;
  }
}

export function parseOpenTarget(raw: string): { path: string } | null {
  let input = raw.trim();
  if (!input) return null;

  if (input.startsWith('nostr:')) {
    input = input.slice('nostr:'.length);
  }

  // A bare NIP-19 identifier.
  const direct = nip19Path(input);
  if (direct) return { path: direct };

  // An identifier embedded in other text (e.g. a njump/habla URL).
  const tokenMatch = input.match(BECH32_TOKEN);
  if (tokenMatch) {
    const embedded = nip19Path(tokenMatch[0]);
    if (embedded) return { path: embedded };
  }

  // A bare hex event id.
  if (HEX_64.test(input)) {
    return { path: `/${nip19.noteEncode(input.toLowerCase())}` };
  }

  // URLs — from Nostr clients (hex paths) or the plain web.
  const url = sanitizeUrl(input) ?? sanitizeUrl(`https://${input}`);
  if (url) {
    try {
      const parsed = new URL(url);

      // Primal-style event/profile links.
      const eventMatch = parsed.pathname.match(/\/e\/([0-9a-f]{64})/i);
      if (eventMatch) {
        return { path: `/${nip19.noteEncode(eventMatch[1].toLowerCase())}` };
      }
      const profileMatch = parsed.pathname.match(/\/p\/([0-9a-f]{64})/i);
      if (profileMatch) {
        return { path: `/${nip19.npubEncode(profileMatch[1].toLowerCase())}` };
      }

      // Any other web page → its highlights page (canonical URL form).
      return { path: `/web/${encodeURIComponent(normalizeUrl(parsed.href) ?? parsed.href)}` };
    } catch {
      return null;
    }
  }

  return null;
}
